import { useI18n } from '@whizz/react-i18n';
import React, { useContext, useState } from 'react';
import type { HTMLAttributes } from 'react';
import type { AllColorType } from '../../../util/types';
import { Alert, Button, ButtonIcon } from '../../../components';
import { InfoButton } from '../../../global';
import HighlightCard from './HighlightCard';
import { GLOBAL_ICONS } from '../../../util/vars';
import { PdfContext } from '../../../context/PdfContext';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;

interface Props extends HTMLProps {
  title: string;
  label?: string | null;
  /**
   * Numeric or string value of the section.
   */
  value?: number | string | null;
  /**
   * Shows highlighted student(s).
   */
  highlightLabel?: string | null;
  /**
   * Sets highlighted student(s) value(s).
   */
  highlightValue?: number | string | null;
  /**
   * Shows non-filtered students/classes average.
   */
  othersLabel?: string | null;
  /**
   * Sets non-filtered students/classes value.
   */
  othersValue?: number | string | null;
  /**
   * Unit after value.
   */
  unit?: string;
  /**
   * Description after value.
   */
  description?: string;
  /**
   * Adds a toggle button in order to expand/collapse the chart.
   */
  canBeExpanded?: boolean;
  /**
   * Disables toggle button of charts.
   */
  disableChartToggle?: boolean;
  /**
   * Adds content for info alert.
   */
  moreInfo?: string;
  /**
   * Optional element to render, eg. custom button
   */
  additionalAction?: React.ReactNode;
  /**
   * Optional element to render instead the title, eg. dropdown
   */
  overrideTitle?: React.ReactNode;
  /**
   * Allow to override the bg-color
   */
  overrideColor?: AllColorType | null;
  /**
   * Hides the class average
   */
  hideAverage?: boolean
  /**
   * Section content.
   */
  children?: React.ReactNode;
  /**
   * Renders the chart expanded initially.
   */
  chartIsExpanded?: boolean;
}

type WithId = { id: number };

function getMean<T>(
  values: T[],
  valueGetter: (value: T) => number | null | undefined,
): number | null {
  // If there are no items at all, return null (show dash)
  if (values.length === 0) {
    return null;
  }

  const numbers = values
    .map(valueGetter)
    .filter((value): value is number => value !== null && value !== undefined && !Number.isNaN(value));

  // If there are items but no valid data points, return null (show dash)
  if (numbers.length === 0) {
    return null;
  }

  const total = numbers.reduce((sum, value) => sum + value, 0);
  // Return the actual average (could be 0, which is "genuinely zero")
  return total / numbers.length;
}

export function getFilteredAverage<T extends WithId>(
  values: T[],
  highlightedIds: number[],
  valueGetter: (value: T) => number | null | undefined,
): number | null {
  if (!highlightedIds.length) {
    return null;
  }

  return getMean(values.filter((value) => highlightedIds.includes(value.id)), valueGetter);
}

export function getOthersAverage<T extends WithId>(
  values: T[],
  highlightedIds: number[],
  valueGetter: (value: T) => number | null | undefined,
): number | null {
  if (!highlightedIds.length) {
    return null;
  }

  return getMean(values.filter((value) => !highlightedIds.includes(value.id)), valueGetter);
}

const SectionLayout = ({
  title,
  label,
  value,
  highlightLabel,
  highlightValue,
  othersLabel,
  othersValue,
  unit,
  description,
  canBeExpanded = true,
  disableChartToggle = false,
  moreInfo,
  additionalAction,
  overrideColor,
  overrideTitle,
  hideAverage = false,
  chartIsExpanded = false,
  children,
  ...other
}: Props): JSX.Element => {
  const { translate } = useI18n();

  const [ showSection, setShowSection ] = useState(true);
  const [ showInfo, setShowInfo ] = useState(false);
  const [ showChart, setShowChart ] = useState(chartIsExpanded);

  const { isPdf } = useContext(PdfContext);
  const showAverage = !hideAverage && value != null;
  const showOthers = othersLabel != null;
  const showPerOutside = showAverage && highlightLabel;

  const MoreInfoComponent = () => (
    <div className={'ml-md'}>
      <InfoButton
        onClick={(event: MouseEvent) => {
          event.stopPropagation();
          setShowInfo(!showInfo);
        }}
      />
    </div>
  );

  return (
    <div {...other as HTMLProps} className={`${isPdf ? 'flex p-sm' : ''} rounded-default bg-white shadow-sm ${'className' in other ? other.className : ''}`}>
      {showSection && (
        <div>
          <div
            className={'container flex items-center justify-between border-b'}
            onClick={() => {
              if (canBeExpanded) {
                setShowChart(!showChart);
              }
            }}
          >
            <div className={'flex items-center text-xl leading-6'}>
              {overrideTitle ??
                <div className={'rounded-default flex items-center'}>
                  <div className={'flex items-center font-bold'}>
                    <span className={'uppercase'}>
                      {title}
                    </span>
                  </div>
                </div>
              }
              <div className={'flex items-center font-bold'}>
                <HighlightCard
                  type='average'
                  label={label}
                  value={value}
                  unit={unit}
                  description={showPerOutside ? null : description}
                  overrideColor={overrideColor}
                  show={showAverage}
                >
                  {moreInfo && !isPdf && !highlightLabel && <MoreInfoComponent />}
                </HighlightCard>
                <HighlightCard
                  type='average'
                  label={othersLabel}
                  value={othersValue}
                  unit={unit}
                  description={showPerOutside ? null : description}
                  overrideColor='primary'
                  show={showOthers}
                />
                <HighlightCard
                  label={highlightLabel}
                  value={highlightValue}
                  unit={unit}
                  description={description}
                  highlight
                  show={!!highlightLabel}
                  data-cy="highlight-card"
                >
                  {moreInfo && !isPdf && <MoreInfoComponent />}
                </HighlightCard>
              </div>
              {moreInfo && !isPdf && !showAverage && !highlightLabel && <MoreInfoComponent />}
            </div>
            <div className={'flex'}>
              {canBeExpanded && !isPdf && (
                <ButtonIcon
                  color={'transparent'}
                  disabled={disableChartToggle}
                  icon={GLOBAL_ICONS[showChart ? 'collapse_on' : 'collapse_off']}
                  hasPadding={false}
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowChart(!showChart);
                  }}
                  data-cy="student-chart-button"
                  data-hide-print
                />
              )}
              {additionalAction && (
                <div className={'ml-md'}>
                  {additionalAction}
                </div>
              )}
            </div>
          </div>
          <Alert show={showInfo} className={'w-full lg:w-2/4 p-4'} data-cy="more-info">
            {moreInfo && (
              <div
                dangerouslySetInnerHTML={{
                  __html: moreInfo,
                }}
              />
            )}
          </Alert>
          {(showChart || false === canBeExpanded) && (
            <div className={'container relative'}>
              {children}
            </div>
          )}
        </div>
      )}
      {!showSection && (
        <div className={'container'}>
          <Button
            color={'dark'}
            asLink
            onClick={() => setShowSection(!showSection)}
            data-cy="show-section"
          >
            {`${translate('components.BUTTONS.SHOW')} ${title}`}
          </Button>
        </div>
      )}
    </div>
  );
};

SectionLayout.displayName = 'SectionLayout';

export default SectionLayout;
